import { useEffect, useRef, useState } from 'react'
import translate from 'translate'
import { dashboardI18n } from './visitorPortalI18n'

/* ───────────────────────────────────────────────────────────────
   Translation for the visitor dashboards.

   Two layers, in priority order:

   1. The hand-verified dictionary in visitorPortalI18n.js
      (English → Gujarati / Hindi). These are correct, so they are
      used as-is with NO network call. This is the "default / override"
      source you asked for.

   2. Anything NOT in the dictionary — e.g. dynamic data that comes
      from API calls (plan-a-visit, meals, spaces, etc.) — is
      translated on the fly with the `translate` package (free Google
      endpoint, no API key), then cached in localStorage so it is
      instant next time.

   Usage:
     const { t, tDeep } = useTranslate(lang)   // 'en' | 'gu' | 'hi'
     t('VISITOR DASHBOARD')          // dictionary → exact gu/hi value
     t(apiPlace.name)                // not in dict → auto-translated
     t('Submit', 'સબમિટ')           // explicit per-call override

   t(text, override):
     - lang === 'en'        → returns `text` unchanged
     - override provided    → returns `override`
     - dictionary has it    → returns the hand-verified translation
     - auto-translate cache → returns the cached translation
     - otherwise            → returns English `text` for now and
                              queues a background translation
   ─────────────────────────────────────────────────────────────── */

translate.engine = 'google' // free translate.googleapis.com endpoint
translate.from = 'en'

const LS_KEY = 'dashboardTranslations.v1'

// Cache of AUTO-translated (non-dictionary) strings: { gu: {en: tr}, hi: {} }
let cache = {}
try {
  cache = JSON.parse(localStorage.getItem(LS_KEY)) || {}
} catch {
  cache = {}
}
const persist = () => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(cache))
  } catch {
    /* storage unavailable — keep in-memory cache only */
  }
}

// Build the English-text → translation map from the existing dictionary by
// walking en / gu / hi in lockstep (same key path). This lets t() look up a
// verified translation by the English string it is called with.
const DICT = { gu: {}, hi: {} }
const buildDict = (en, gu, hi) => {
  if (typeof en === 'string') {
    if (typeof gu === 'string') DICT.gu[en] = gu
    if (typeof hi === 'string') DICT.hi[en] = hi
    return
  }
  if (Array.isArray(en)) {
    en.forEach((v, i) => buildDict(v, gu && gu[i], hi && hi[i]))
    return
  }
  if (en && typeof en === 'object') {
    for (const k of Object.keys(en)) {
      buildDict(en[k], gu && gu[k], hi && hi[k])
    }
  }
}
buildDict(dashboardI18n.en, dashboardI18n.gu, dashboardI18n.hi)

const dictHas = (lang, text) =>
  (lang === 'gu' || lang === 'hi') && Object.prototype.hasOwnProperty.call(DICT[lang], text)

// Proper nouns that Google transliterates wrongly inside otherwise-fine
// sentences (e.g. "Anekant Library" → "અનિકાંત પુસ્તકાલય"). We can't fix these
// with a dictionary entry because the surrounding text is dynamic API data, so
// the correction is applied to the finished translation instead. This also
// repairs values already sitting in the localStorage cache.
const PROPER_NOUN_FIXUPS = {
  gu: [
    [/અનિકાંત|અનીકાંત|અનિકાન્ત|અનેકાન્ત/g, 'અનેકાંત'],
  ],
  hi: [
    [/अनिकांत|अनीकांत|अनिकान्त|अनेकान्त/g, 'अनेकांत'],
  ],
}
const fixProperNouns = (lang, text) => {
  const rules = PROPER_NOUN_FIXUPS[lang]
  if (!rules || typeof text !== 'string') return text
  return rules.reduce((out, [re, correct]) => out.replace(re, correct), text)
}

// Translate `items` to `lang` with limited concurrency so we don't hammer the
// free Google endpoint (which rate-limits aggressive parallel bursts).
const translateBatch = async (items, lang, store) => {
  const CONCURRENCY = 6
  for (let i = 0; i < items.length; i += CONCURRENCY) {
    const slice = items.slice(i, i + CONCURRENCY)
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(
      slice.map(async (text) => {
        try {
          store[text] = fixProperNouns(lang, await translate(text, lang))
        } catch {
          /* leave untranslated — t() keeps returning the English text */
        }
      })
    )
  }
}

export function useTranslate(lang) {
  const [, force] = useState(0)
  // Strings requested via t() this render that have no dictionary/cache hit.
  // These are auto-translated after render (covers dynamic API data).
  const pending = useRef(new Set())

  // Runs after every render: translate whatever t() flagged as missing.
  useEffect(() => {
    if (!lang || lang === 'en') {
      pending.current.clear()
      return undefined
    }
    const store = cache[lang] || (cache[lang] = {})
    const todo = [...pending.current].filter((s) => !(s in store))
    pending.current.clear()
    if (todo.length === 0) return undefined

    let cancelled = false
    ;(async () => {
      await translateBatch(todo, lang, store)
      if (cancelled) return
      persist()
      force((n) => n + 1) // re-render so the new translations show
    })()
    return () => {
      cancelled = true
    }
  })

  const t = (text, override) => {
    if (typeof text !== 'string') return text
    if (lang === 'en') return text
    if (override !== undefined && override !== null) return override
    // Clock times (e.g. "9:00 am", "5:00 PM") are never translated — auto-translate
    // turns PM times into verbose phrases ("સાંજે 5:00 કલાકે") while leaving AM ones
    // alone, which is inconsistent and breaks fixed-width layouts. Keep them as-is.
    if (/^\d{1,2}:\d{2}\s*(?:[ap]\.?m\.?)?$/i.test(text.trim())) return text
    if (dictHas(lang, text)) return DICT[lang][text]
    const store = cache[lang]
    if (store && store[text] !== undefined) return fixProperNouns(lang, store[text])
    // Not known yet — queue for background translation, show English meanwhile.
    if (text.trim()) pending.current.add(text)
    return text
  }

  // Deep-translate the display text inside a data structure (object/array) —
  // e.g. data coming from API calls. Non-display fields (urls, ids, prices,
  // icons) and non-text strings (urls, times, pure numbers/codes) are left
  // untouched so we never translate a URL, a time like "9:00 AM", or a code.
  const SKIP_KEYS = new Set([
    'img', 'href', 'icon', 'id', 'key', 'value', 'price', 'url', 'link', 'mapUrl', 'image',
    'images', 'extraImages',
    'color', 'bg', 'background', 'colour', 'hex', 'style', 'badge', 'type', 'code', 'status',
    // Durations like "1h" / "45 min" are localized by a dedicated formatter
    // (fmtDuration) at the call site — auto-translate mangles them ("1h" → "1 ક").
    'durationLabel', 'needToStay',
  ])
  const isTranslatable = (s) => {
    const v = s.trim()
    if (!v) return false
    if (/^https?:\/\//i.test(v) || /^www\./i.test(v)) return false // urls
    if (/[<>]/.test(v)) return false // html markup — never translate (would mangle tags)
    if (/^(?:#|rgba?\(|hsla?\()/i.test(v)) return false // colors (#B45309, rgba(...))
    if (!/[A-Za-z]/.test(v)) return false // no Latin letters: numbers, codes, or already translated
    if (/^\d{1,2}:\d{2}\s*(?:[AP]M)?$/i.test(v)) return false // clock times
    return true
  }
  const tDeep = (node, parentKey) => {
    if (typeof node === 'string') {
      return SKIP_KEYS.has(parentKey) || !isTranslatable(node) ? node : t(node)
    }
    // Array items keep their parent's key, so a skipped key (e.g. `images`)
    // protects its whole list — otherwise the items would lose that context and
    // get translated one by one.
    if (Array.isArray(node)) return node.map((v) => tDeep(v, parentKey))
    if (node && typeof node === 'object') {
      const out = {}
      for (const k of Object.keys(node)) out[k] = tDeep(node[k], k)
      return out
    }
    return node
  }

  return { t, tDeep }
}

export default useTranslate
